import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Check, X } from "lucide-react";
import { api, formatApiError, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isPlatformAuthEnabled } from "@/lib/flags";
import {
  passwordPolicyChecks,
  isPasswordValid,
  passwordsMatch,
  PASSWORD_MIN_LENGTH,
} from "@/lib/passwordPolicy";
import { toast } from "sonner";

/**
 * Fase 1F — Aceite de convite dual-mode.
 * - Legado: pré-busca ``/auth/invite/{token}`` e envia ``/auth/accept-invite``.
 * - Global: envia ``/auth/accept-invite`` (reescrito para
 *   ``/api/platform/auth/accept-invite`` pelo interceptor). Não faz
 *   pré-busca — o backend global não possui endpoint público de
 *   consulta por token, e o token NUNCA vai a logs.
 */
export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const platform = isPlatformAuthEnabled();

  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (!token) { setError("Token ausente"); return; }
    if (platform) {
      // Modo global: apenas mostra o formulário (sem pré-busca).
      // ``invite`` recebe um placeholder para renderizar o form.
      setInvite({ platform: true });
      return;
    }
    // Modo legado: pré-busca detalhes do convite.
    api.get(`/auth/invite/${token}`)
      .then(({ data }) => { setInvite(data); setForm((f) => ({ ...f, name: data.name || "" })); })
      .catch((e) => setError(formatApiError(e)));
  }, [token, platform]);

  const checks = passwordPolicyChecks(form.password);
  const pwdValid = isPasswordValid(form.password);
  const matches = passwordsMatch(form.password, form.confirm);
  const canSubmit = pwdValid && matches && !loading;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!pwdValid) {
      setError(`A senha não atende aos critérios (mínimo ${PASSWORD_MIN_LENGTH} caracteres).`);
      return;
    }
    if (!matches) {
      setError("A confirmação não coincide com a nova senha.");
      return;
    }
    setLoading(true); setError("");
    try {
      await api.post("/auth/accept-invite", {
        token, name: form.name, password: form.password,
      });
      // Em modo global, o próprio backend já emitiu cookies de sessão
      // para o tenant aceito — apenas confirmamos via /me.
      await refresh();
      // Limpa os campos após sucesso.
      setForm({ name: "", password: "", confirm: "" });
      toast.success("Conta criada via convite.");
      navigate("/dashboard");
    } catch (err) {
      // Mensagens genéricas em modo global; detalhadas no legado.
      setError(platform
        ? "Convite inválido, expirado ou senha não atende à política."
        : formatApiError(err));
    } finally { setLoading(false); }
  };

  const emailLine = invite && invite.email
    ? <>{invite.email} — função: <span className="text-slate-900 font-medium">{ROLE_LABELS[invite.role]}</span></>
    : "Complete os dados abaixo para aceitar o convite.";

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Aceitar convite</h1>
        <p className="text-sm text-slate-600 mt-2" data-testid="invite-guidance">
          Já possui uma conta no Ruma Flow? Use sua senha atual. Se este é seu primeiro acesso, crie uma senha.
        </p>
        {error && !invite && (
          <div className="mt-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="invite-error">{error}</div>
        )}
        {invite && (
          <>
            <p className="text-sm text-slate-500 mt-1">{emailLine}</p>
            <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="invite-form">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Nome</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="invite-name-input"
                  className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Senha</label>
                <div className="relative">
                  <input
                    type={showPwd ? "text" : "password"}
                    required
                    autoComplete="new-password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="invite-password-input"
                    className="w-full h-10 rounded-lg border border-slate-200 pl-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    aria-label={showPwd ? "Ocultar senha" : "Mostrar senha"}
                    className="absolute inset-y-0 right-0 flex items-center px-2 text-slate-400 hover:text-slate-700"
                    data-testid="invite-password-toggle"
                  >
                    {showPwd ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirmar senha</label>
                <div className="relative">
                  <input
                    type={showConfirm ? "text" : "password"}
                    required
                    autoComplete="new-password"
                    value={form.confirm}
                    onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                    data-testid="invite-confirm-input"
                    className="w-full h-10 rounded-lg border border-slate-200 pl-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    aria-label={showConfirm ? "Ocultar confirmação" : "Mostrar confirmação"}
                    className="absolute inset-y-0 right-0 flex items-center px-2 text-slate-400 hover:text-slate-700"
                    data-testid="invite-confirm-toggle"
                  >
                    {showConfirm ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <ul className="text-xs text-slate-600 space-y-1" data-testid="invite-password-criteria">
                {checks.map((c) => (
                  <li key={c.key} className="flex items-center gap-2">
                    {c.ok
                      ? <Check className="w-3.5 h-3.5 text-emerald-600" aria-hidden="true" />
                      : <X className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" />}
                    <span className={c.ok ? "text-emerald-700" : "text-slate-600"}>{c.label}</span>
                  </li>
                ))}
                <li className="flex items-center gap-2" data-testid="invite-password-match">
                  {matches
                    ? <Check className="w-3.5 h-3.5 text-emerald-600" aria-hidden="true" />
                    : <X className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" />}
                  <span className={matches ? "text-emerald-700" : "text-slate-600"}>
                    As duas senhas precisam ser iguais
                  </span>
                </li>
              </ul>

              {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="invite-submit-button"
                className="w-full h-10 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
              >
                {loading ? "Criando..." : "Aceitar convite"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
